import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {RedirectService, StorageService, UserHttpService} from '@core/services';
import {needsBasicProfile} from '@core/utils/needs-basic-profile.util';
import {FirebaseAuthentication, User} from '@capacitor-firebase/authentication';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  private userHttpService = inject(UserHttpService);
  private redirectService = inject(RedirectService);
  private router = inject(Router);
  private storageService = inject(StorageService);

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    try {
      const firebaseUser = await this.resolveFirebaseUser();
      const storedUser = firebaseUser ? null : await this.storageService.get('user');
      const user = firebaseUser ?? (storedUser?.uid ? storedUser : null);

      if (!user) {
        await this.redirectService.setRedirectUrl(state.url);
        return this.router.createUrlTree([`/login`], {
          queryParams: {
            nextPageUrl: state.url,
            page: route?.routeConfig?.path
          }
        });
      }

      const onSetBasicProfile = state.url.includes('/set-basic-profile');
      const clientUser = await this.userHttpService.ensureSession(user);

      if (!clientUser) {
        await this.redirectService.setRedirectUrl(state.url);
        return this.router.createUrlTree([`/login`], {
          queryParams: {
            nextPageUrl: state.url,
            page: route?.routeConfig?.path
          }
        });
      }

      if (needsBasicProfile(clientUser) && !onSetBasicProfile) {
        await this.redirectService.setRedirectUrl(state.url);
        return this.router.createUrlTree(['/set-basic-profile']);
      }

      if (!needsBasicProfile(clientUser) && onSetBasicProfile) {
        const redirectUrl = await this.redirectService.getAndClearRedirectUrl();
        if (redirectUrl) {
          return this.router.parseUrl(redirectUrl);
        }
        return this.router.createUrlTree(['/home-tabs']);
      }

      return true;
    } catch (error) {
      console.error('Error during authentication guard', error);
      return this.router.createUrlTree(['/login']);
    }
  }

  private async resolveFirebaseUser(attempts = 5, delayMs = 200): Promise<User | null> {
    for (let attempt = 0; attempt < attempts; attempt++) {
      const result = await FirebaseAuthentication.getCurrentUser();
      if (result.user) {
        return result.user;
      }

      if (attempt < attempts - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }

    return null;
  }
}
